import React, { useCallback, useMemo } from 'react'
import { parse, stringify } from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { EVIDENCE_WINDOW_STATUS } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { formatISODateTime } from 'src/Utils/formatDate'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { PopupButtonLabel } from 'src/components/Reusable/Buttons/PopupButton/PopupButton.styles'
import { Message } from 'src/components/Reusable/Message/Message'
import { MessageType } from 'src/components/Reusable/Message/Message.constants'
import { Pill } from 'src/components/Reusable/Pill'
import palette from 'src/designSystem/variables/palette'
import {
  OutOfWindowDateButton,
  OutOfWindowNoticeContainer,
  PillTooltipTarget,
} from './evidence.styles'
import {
  getEvidenceWindowTooltip,
  isEvidenceOutOfWindow,
  isShowingAllDates,
} from './evidence.helpers'
import {
  OUT_OF_WINDOW_HIDE_LABEL,
  OUT_OF_WINDOW_PILL_LABEL,
  OUT_OF_WINDOW_SHOW_ALL_LABEL,
  SHOW_ALL_DATES_PARAM,
} from './evidence.constants'

// Out-of-window presentation, kept out of evidence.components so surfaces that need only
// these can import them without pulling in EvidenceTabs and the show-page tree behind it.
// evidence.components re-exports all three, so existing importers are unaffected.

// The Relevant Date where the reader cannot edit it — automated evidence in either grid,
// and the link-existing picker. Read-only is not a reason to say nothing: if a control test
// would pass this evidence over, the date still carries the amber badge and the reason.
// No edit pencil and no hover, so it does not read as an editor.
//
// InWindowWrapper lets a caller keep its own cell styling for the ordinary case; the badge
// brings its own container, so it is only applied to bare text.
export const ReadOnlyRelevantDate = ({
  relevantDate,
  windowStatus = EVIDENCE_WINDOW_STATUS.IN_WINDOW,
  tooltipId,
  InWindowWrapper = React.Fragment,
}: {
  relevantDate: Timestamp
  windowStatus?: EVIDENCE_WINDOW_STATUS
  tooltipId: string
  InWindowWrapper?: React.ElementType
}): JSX.Element => {
  const formatted = formatISODateTime(relevantDate)
  if (!isEvidenceOutOfWindow(windowStatus)) {
    return <InWindowWrapper>{formatted}</InWindowWrapper>
  }
  return (
    <Tooltip
      id={`${tooltipId}-window-status`}
      tooltipBody={getEvidenceWindowTooltip(windowStatus)}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
    >
      <OutOfWindowDateButton $size="small" $isDisabled>
        <PopupButtonLabel $size="small">{formatted}</PopupButtonLabel>
      </OutOfWindowDateButton>
    </Tooltip>
  )
}

// The evidence show page's badge for the version on screen. Sits by the title because it
// describes that version — the notice it replaced counted every out-of-window evidence in
// the account, which was never what a reader on this page wanted to know.
export const OutOfWindowPill = ({
  windowStatus,
  tooltipId,
}: {
  windowStatus: EVIDENCE_WINDOW_STATUS
  tooltipId: string
}): JSX.Element | null => {
  if (!isEvidenceOutOfWindow(windowStatus)) return null
  return (
    <Tooltip
      id={`${tooltipId}-window-pill`}
      tooltipBody={getEvidenceWindowTooltip(windowStatus)}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
    >
      <PillTooltipTarget>
        <Pill
          label={OUT_OF_WINDOW_PILL_LABEL}
          backgroundColor={palette.orange[30]}
          borderColor={palette.orange[700]}
          useBorder
          $size="small"
          $height="24px"
        />
      </PillTooltipTarget>
    </Tooltip>
  )
}

// OutOfWindowNotice offers to reveal evidence a control test would ignore. Rendered above
// the grid on the evidence index and the control evidence tab. Nothing shows when the
// window excluded nothing and the reader has not already opted in.
export const OutOfWindowNotice = ({
  outOfWindowCount,
  inset = false,
}: {
  outOfWindowCount: number
  // Set on surfaces that render this outside a GridContainer, so it lines up with the rest
  // of the page rather than sitting flush against the left edge.
  inset?: boolean
}): JSX.Element | null => {
  // Read the toggle through NavigateContext, falling back to the router when no provider
  // is present. On a page that is the URL; inside a modal that supplies its own provider
  // — LinkExistingEvidenceModal does — it is the modal's local state, so toggling filters
  // the picker without touching the route or the list behind it.
  const { navigateState } = useNavigateContext()
  const navigateFallback = useNavigate()
  const navigate = navigateState.navigate ?? navigateFallback
  const location = useLocation()
  const queryParamsFallback = useMemo(
    () => parse(location.search, { arrayFormat: 'bracket' }),
    [location.search],
  )
  const queryParams = navigateState.parsedQueryParams ?? queryParamsFallback
  const isShowingAll = isShowingAllDates(queryParams)
  // Show All widens the list past what a control test considers, so it is an authoring
  // affordance rather than a reading one. Roles without it are not shown the notice at
  // all: its only purpose is to offer the toggle, and a count of evidence they cannot
  // reveal reads as a dead end. The server narrows the request to match.
  const canViewOutOfRange = useCanPerformAction(
    CAPABILITY.CAN_VIEW_OUT_OF_RANGE_EVIDENCE,
  )

  const toggle = useCallback(() => {
    const next = { ...queryParams }
    if (isShowingAll) {
      delete next[SHOW_ALL_DATES_PARAM]
    } else {
      next[SHOW_ALL_DATES_PARAM] = 'true'
    }
    navigate(
      `${location.pathname}?${stringify(next, { arrayFormat: 'bracket' })}`,
    )
  }, [navigate, location.pathname, queryParams, isShowingAll])

  // Nothing outside the range means nothing to say, whichever way the toggle is set. With
  // Show All on and a zero count there is genuinely no out-of-range evidence, so the notice
  // would only announce itself.
  if (!outOfWindowCount) return null
  if (!canViewOutOfRange) return null

  // Deliberately no onDismiss: that is what renders Message's close button, and this
  // notice explains why the list below is short. Dismissing it would leave a list that
  // silently omits evidence.
  return (
    <OutOfWindowNoticeContainer $inset={inset}>
      <Message
        messageType={MessageType.WARNING}
        messageText={`${outOfWindowCount} evidence outside the current date range`}
        ctaButton={{
          text: isShowingAll
            ? OUT_OF_WINDOW_HIDE_LABEL
            : OUT_OF_WINDOW_SHOW_ALL_LABEL,
          onClick: toggle,
          withMargin: true,
        }}
      />
    </OutOfWindowNoticeContainer>
  )
}
